import React from 'react';
import ReactDOM from 'react-dom/client';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeWrapper } from './app/ThemeWrapper';
import { LoadingProvider } from './app/LoadingProvider';
import config from './app/config';

import { PostHogProvider } from 'posthog-js/react'
import { ErrorBoundary } from './app/GlobalErrorBoundary';
import { ErrorReporter } from '@/app/tracking';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { fetchInterceptor } from '@/app/fetchInterceptor';
import App from './app/App';

import './index.css';

import * as Sentry from "@sentry/react";

Sentry.init({
  dsn: "https://cd8d71942b2f8acaeb6fea5fe7d0a7a8@o4509793395605504.ingest.us.sentry.io/4509793439645696",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true
});


const handlePromiseRejection = (event) => {
  const error = event.reason;
  const errorInfo = {
    message: error?.message || 'Unknown error',
    stack: error?.stack || 'No stack trace available',
    name: error?.name || 'Error',
    // Extract any other useful properties from the error
    additionalInfo: extractErrorDetails(error)
  };

  console.error('Unhandled promise rejection:', errorInfo);

  // Check if ErrorReporter is available before using it
  if (typeof ErrorReporter !== 'undefined') {
    ErrorReporter.error(`Unhandled promise rejection: ${errorInfo.name} - ${errorInfo.message}\n${errorInfo.stack}`);
  }
};

const handleError = (event) => {
  // Extract useful information from the ErrorEvent
  const errorInfo = {
    message: event.error?.message || event.message || 'Unknown error',
    stack: event.error?.stack || 'No stack trace available',
    filename: event.filename || 'Unknown file',
    lineno: event.lineno || 'Unknown line',
    colno: event.colno || 'Unknown column',
    type: event.type,
    // Extract any other useful properties from the error
    additionalInfo: extractErrorDetails(event.error)
  };

  console.error('Global error:', errorInfo);

  // Check if ErrorReporter is available before using it
  if (typeof ErrorReporter !== 'undefined') {
    ErrorReporter.error(`Global error in ${errorInfo.filename}:${errorInfo.lineno} - ${errorInfo.message}\n${errorInfo.stack}`);
  }
};

window.addEventListener('unhandledrejection', handlePromiseRejection);
window.addEventListener('error', handleError);


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Global defaults if you like
      retry: (failureCount, error) => {
        // Don't retry on authentication errors (401)
        if (error?.errorCode === 401 || error?.name === 'AuthenticationError') {
          return false;
        }
        // Don't retry on 4xx client errors 
        if (error?.errorCode >= 400 && error?.errorCode < 500) {
          return false;
        }
        // Retry once for other errors
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
    },
  },
});

const posthog_options = {
  api_host: config.POSTHOG_HOST,
  // Exclude specific URL parameters
  excluded_query_params: [
    'token',
  ],
  session_recording: {
    maskAllInputs: false,
    maskInputOptions: {
      password: true, // Highly recommended as a minimum!!
      // color: false,
      // date: false,
      // 'datetime-local': false,
      // email: false,
      // month: false,
      // number: false,
      // range: false,
      // search: false,
      // tel: false,
      // text: false,
      // time: false,
      // url: false,
      // week: false,
      // textarea: false,
      // select: false,
    }
  }
}

// Helper function to extract additional error details
const extractErrorDetails = (error) => {
  if (!error) return {};

  // Create a copy of the error object without circular references
  const details = {};

  // Try to extract all enumerable properties
  try {
    // Get all enumerable properties
    for (const key in error) {
      try {
        // Skip common properties that we've already handled
        if (['message', 'stack', 'name'].includes(key)) continue;

        const value = error[key];
        // Avoid circular references and complex objects
        if (typeof value !== 'object' || value === null) {
          details[key] = value;
        } else {
          // For objects, just note the type to avoid circular references
          details[key] = `[object ${Object.prototype.toString.call(value).slice(8, -1)}]`;
        }
      } catch (e) {
        details[key] = 'Error accessing property';
      }
    }
  } catch (e) {
    details.extractionError = 'Error extracting details from error object';
  }

  return details;
};


fetchInterceptor();

const disableStrictMode = false;

const AppRoot = () => (
  <PostHogProvider apiKey={config.POSTHOG_KEY} options={posthog_options}>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <LoadingProvider>
          <HelmetProvider>
            <BrowserRouter>
              <ThemeWrapper>
                <App />
              </ThemeWrapper>
            </BrowserRouter>
          </HelmetProvider>
        </LoadingProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  </PostHogProvider>
);

ReactDOM.createRoot(document.getElementById('root')).render(
  disableStrictMode ? <AppRoot /> : <React.StrictMode><AppRoot /></React.StrictMode>
);
